import React, { useContext } from 'react';
import { default as NextLink } from 'next/link';
import { convertAbsoluteLinkToRelative, scrollIntoView } from '@/utils';
import { Box, SystemFeaturesContext } from '@/components';
import { AnimationProps } from '../animation/Box';
import { LinkModel } from '@/types';
import { getSecureDownloadLink } from '@/api';
import { useRouter } from 'next/router';

interface ILinkProps extends AnimationProps {
  link: LinkModel;
  children?: React.ReactNode;
  ariaLabel?: string;
  className?: string;
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

const Link = ({ animate = false, animation, link, children, ariaLabel, onClick, ...props }: ILinkProps) => {
  const router = useRouter();
  const { realHost } = useContext(SystemFeaturesContext);

  if (!link) return;

  const fixedLink = convertAbsoluteLinkToRelative(link, realHost);

  const _onClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (link.href.includes('/securedownloadlink?')) {
      // Not tested!
      e.preventDefault();
      const sdl = await getSecureDownloadLink(link.href);
      router.push(sdl.data.redirectTo);
    } else if (link.href.length > 1 && link.href.startsWith('#')) {
      // Handle anchor links
      const style = window.getComputedStyle(document.body);
      if (window.lenis) {
        window.lenis.scrollTo(link.href, { offset: parseInt(style.getPropertyValue('--header-height'), 10) * -1 });
      } else {
        const element = document.querySelector(link.href) as HTMLElement | null;
        if (element) {
          scrollIntoView(element, parseInt(style.getPropertyValue('--header-height'), 10) * -1);
        }
      }
    }
  };

  const linkObj = (
    <NextLink
      href={fixedLink?.href || ''}
      target={fixedLink?.target || undefined}
      aria-label={fixedLink?.target === '_blank' ? ariaLabel || link?.text || undefined : undefined}
      onClick={_onClick}
      locale={false}
      {...props}
    >
      {children || link?.text}
    </NextLink>
  );

  return !animate && !animation ? (
    linkObj
  ) : (
    <Box animate={animate} animation={animation}>
      {linkObj}
    </Box>
  );
};

export default Link;
